/**
 * useDragReorder — Pointer-event based drag reorder hook.
 *
 * Uses pointermove/pointerup on document for cross-element tracking.
 * Does NOT use HTML5 drag API (incompatible with mobile + artifact preview).
 */

import { useState, useRef, useCallback } from 'react';

export function useDragReorder(onReorder: (from: number, to: number) => void) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const startDrag = useCallback((idx: number) => {
    setDragIdx(idx);
    setOverIdx(idx);

    const onMove = (ev: PointerEvent) => {
      const y = ev.clientY;
      for (let i = 0; i < cardRefs.current.length; i++) {
        const el = cardRefs.current[i];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (y >= r.top && y <= r.bottom) {
          setOverIdx(i);
          break;
        }
      }
    };

    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setDragIdx(prev => {
        setOverIdx(cur => {
          if (prev !== null && cur !== null && prev !== cur) onReorder(prev, cur);
          return null;
        });
        return null;
      });
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [onReorder]);

  return { dragIdx, overIdx, startDrag, cardRefs };
}
